import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
  deleteDoc,
  writeBatch,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import {
  ArrowLeft,
  Download,
  Folder,
  FolderPlus,
  Plus,
  FileText,
  Image as ImageIcon,
  Eye,
  Bookmark,
  Trash2,
  ChevronRight,
  Search,
  X,
  CheckSquare,
  Square,
  FolderInput,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import { auth, db, storage } from "../firebase";
import { addTimelineEvent } from "../services/timelineService";

const initialCaseData = {
  status: "",
  caseNumber: "",
  caseType: "",
  caseYear: "",
  caseFiledDate: null,
  nextHearingDate: null,
  previousHearingDate: null,
  appearingFor: "",
  court: "",
  customCourt: "",
  petitionerName: "",
  respondentName: "",
  partyContactNumber: "",
  caseNotes: "",
  files: [],
};

function CaseFiles() {
  const API_BASE_URL = "https://jncltt.valscotech.com";

  const navigation = useNavigate();
  const [caseNumber, setCaseNumber] = useState(null);
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [caseData, setCaseData] = useState(initialCaseData);
  const fileInputRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [bookmarkedFiles, setBookmarkedFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null); // null = root level
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [fileDetails, setFileDetails] = useState([]); // store full file docs with folderId info
  const [deleteFolderModal, setDeleteFolderModal] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState(null);
  const [draggingFile, setDraggingFile] = useState(null);
  const [dragOverFolderId, setDragOverFolderId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  const [showBulkMoveModal, setShowBulkMoveModal] = useState(false);
  const [bulkMoving, setBulkMoving] = useState(false);

  // Check where the user came from
  const fromPage = searchParams.get('from');

  // Helper to determine which Firestore collection the case is in
  const getCaseCollection = async () => {
    const pendingSnap = await getDoc(doc(db, "pending", id));
    if (pendingSnap.exists()) return "pending";
    return "disposed";
  };
  

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const user = auth.currentUser;
        if (!user) {
          setError("User not authenticated.");
          setLoading(false);
          return;
        }
        const uid = user.uid;

        // First, get the case document to verify permissions and get case details
        const pendingCaseDoc = doc(db, "pending", id);
        const disposedCaseDoc = doc(db, "disposed", id);
        const pendingCaseSnapshot = await getDoc(pendingCaseDoc);
        let caseDocData;
        let caseNumber;

        if (pendingCaseSnapshot.exists()) {
          caseDocData = pendingCaseSnapshot.data();
          caseNumber = caseDocData.caseNumber || caseDocData.caseNo;
          setCaseNumber(caseNumber);


        } else {
          const disposedCaseSnapshot = await getDoc(disposedCaseDoc);
          if (disposedCaseSnapshot.exists()) {
            caseDocData = disposedCaseSnapshot.data();
            caseNumber = caseDocData.caseNumber || caseDocData.caseNo;
            setCaseNumber(caseNumber);


          } else {
            setError("No such case found!");
            setCaseData(null);
            setFiles([]);
            setLoading(false);
            return;
          }
        }

        // Check permissions
        if (caseDocData.owner !== uid && (!caseDocData.team || !caseDocData.team.includes(uid))) {
          setError("You do not have permission to view this case's files.");
          setCaseData(null);
          setFiles([]);
          setLoading(false);
          return;
        }

        // Set case data
        setCaseData({
          caseNumber: caseNumber,
          caseName: caseDocData.caseType,
          partyName: caseDocData.petitionerName,
        });

        // Now fetch files from the 'files' collection for this case
        const filesCollectionRef = collection(db, "files");
        if (!caseNumber) {
  console.error("Missing caseNumber for case:", id);
  setFiles([]);
  setLoading(false);
  return;
}

const caseIdFilesQuery = query(
  filesCollectionRef,
  where("caseId", "==", id)
);
const caseIdFilesSnapshot = await getDocs(caseIdFilesQuery);
const caseNoFilesQuery = query(
  filesCollectionRef,
  where("caseNo", "==", caseNumber)
);
const caseNoFilesSnapshot = await getDocs(caseNoFilesQuery);



        // 1️⃣ existing logic (KEEP)
const ownFilesQuery = query(
  filesCollectionRef,
  where("caseNo", "==", caseNumber),
  where("lawyer_uid", "==", uid)
);
const ownFilesSnapshot = await getDocs(ownFilesQuery);

// 2️⃣ added logic (NEW)
const allFilesQuery = query(
  filesCollectionRef,
  where("caseNo", "==", caseNumber)
);
const allFilesSnapshot = await getDocs(allFilesQuery);


// 3️⃣ merge results
const fileSet = new Set();
const fileDetailsMap = new Map(); // url -> file doc data

// Helper to collect file details
const collectFile = (d) => {
  const data = d.data();
  if (data.file_url) {
    fileSet.add(data.file_url);
    if (!fileDetailsMap.has(data.file_url)) {
      fileDetailsMap.set(data.file_url, { id: d.id, ...data });
    }
  }
};

// RERA by caseId
caseIdFilesSnapshot.docs.forEach(collectFile);

// RERA fallback by caseNo (CRITICAL)
caseNoFilesSnapshot.docs.forEach(collectFile);

// existing logic (KEEP)
ownFilesSnapshot.docs.forEach(collectFile);

allFilesSnapshot.docs.forEach(collectFile);

// Also include files stored directly in the case document's files array (uploaded during AddCase)
if (caseDocData.files && Array.isArray(caseDocData.files)) {
  caseDocData.files.forEach((url) => {
    if (url && !fileSet.has(url)) {
      fileSet.add(url);
      fileDetailsMap.set(url, {
        id: null,
        file_url: url,
        caseNo: caseNumber,
        caseId: id,
        lawyer_uid: caseDocData.owner || uid,
      });
    }
  });
}

setFiles([...fileSet]);
setFileDetails(Array.from(fileDetailsMap.values()));

// Fetch folders for this case
const foldersQuery = query(
  collection(db, "folders"),
  where("caseId", "==", id)
);
const foldersSnapshot = await getDocs(foldersQuery);
const fetchedFolders = foldersSnapshot.docs.map(d => ({
  id: d.id,
  ...d.data()
}));
setFolders(fetchedFolders);


        // Get bookmarked files from lawyers/{userId}/bookmarks subcollection
        const bookmarksSnapshot = await getDocs(
          query(
            collection(db, "lawyers", uid, "bookmarks"),
            where("caseNo", "==", caseNumber)

          )
        );
        const bookmarkedUrls = bookmarksSnapshot.docs.map(doc => doc.data().fileUrl || doc.data().file_url || "");
        setBookmarkedFiles(bookmarkedUrls.filter(url => url !== ""));

      } catch (err) {
        console.error("Error fetching files:", err);
        setError(
          `Failed to load data. Error: ${err.message || "Unknown error"}`
        );
        setCaseData(null);
        setFiles([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleFileUpload = async (e) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);

      // Filter files by allowed extensions
      const allowedExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'heic'];
      const validFiles = filesArray.filter(file => {
        const extension = file.name.split('.').pop().toLowerCase();
        return allowedExtensions.includes(extension);
      });

      if (validFiles.length !== filesArray.length) {
        toast.error('Some files were not uploaded. Only PDF, JPG, JPEG, PNG, and HEIC files are allowed.');
      }

      if (validFiles.length === 0) {
        return;
      }

      const user = auth.currentUser;
      if (!user) {
        toast.error('User not authenticated');
        return;
      }

      const totalCount = validFiles.length;
      let completedCount = 0;
      let failedCount = 0;
      const uploadToastId = toast.loading(
        totalCount === 1
          ? `Uploading ${validFiles[0].name}...`
          : `Uploading 0 of ${totalCount} files...`
      );

      const uploadPromises = validFiles.map(async (file) => {
        const storageRef = ref(storage, `case-file/${Date.now()}-${file.name}`);

        try {
          await uploadBytes(storageRef, file);
          const url = await getDownloadURL(storageRef);
          completedCount++;
          toast.loading(
            totalCount === 1
              ? `Uploading ${file.name}...`
              : `Uploading ${completedCount} of ${totalCount} files...`,
            { id: uploadToastId }
          );
          return {
            url,
            fileName: file.name,
            size: file.size,
          };
        } catch (error) {
          console.error("upload error", error);
          failedCount++;
          return null;
        }
      });

      const uploadResults = (await Promise.all(uploadPromises)).filter(
        (result) => result !== null
      );

      if (uploadResults.length === 0) {
        toast.error(
          totalCount === 1
            ? `Failed to upload ${validFiles[0].name}`
            : `Failed to upload ${totalCount} files`,
          { id: uploadToastId }
        );
        return;
      }

      if (failedCount > 0) {
        toast.success(
          `Uploaded ${uploadResults.length} of ${totalCount} files (${failedCount} failed)`,
          { id: uploadToastId }
        );
      } else {
        toast.success(
          totalCount === 1
            ? `${uploadResults[0].fileName} uploaded successfully`
            : `${uploadResults.length} files uploaded successfully`,
          { id: uploadToastId }
        );
      }

      // Add files to the 'files' collection
      const filesCollectionRef = collection(db, "files");

      for (const result of uploadResults) {
        try {
          const fileDoc = {
            caseNo: caseData.caseNumber,
            caseId: id,
            fileName: result.fileName,
            file_url: result.url,
            fileSize: result.size || 0,
            lawyer_uid: user.uid,
            uploadedAt: new Date().toISOString(),
          };
          // If inside a folder, tag the file with folderId
          if (currentFolder) {
            fileDoc.folderId = currentFolder.id;
          }
          const docRef = await addDoc(filesCollectionRef, fileDoc);
          // Update local fileDetails state
          setFileDetails(prev => [...prev, { id: docRef.id, ...fileDoc }]);
        } catch (error) {
          console.error('Error adding file document:', error);
        }
      }

      // Update local state
      const urls = uploadResults.map(r => r.url);
      setFiles((prev) => [...prev, ...urls]);

      // Log timeline events for each uploaded file
      const caseCol = await getCaseCollection();
      for (const result of uploadResults) {
        addTimelineEvent(id, caseCol, {
          type: "file_added",
          title: "File uploaded",
          description: `File "${result.fileName}" was uploaded`,
          performedBy: user.uid,
          performedByName: "",
          changes: {
            "File Name": { old: "—", new: result.fileName },
            "Action": { old: "—", new: "Uploaded" },
          },
        });
      }

      // Also update the case document's files array for backward compatibility
      const pendingCaseDoc = doc(db, "pending", id);
      const disposedCaseDoc = doc(db, "disposed", id);
      const pendingCaseSnapshot = await getDoc(pendingCaseDoc);
      const disposedCaseSnapshot = await getDoc(disposedCaseDoc);

      if (pendingCaseSnapshot.exists()) {
        await updateDoc(pendingCaseDoc, {
          files: arrayUnion(...urls),
        });
      } else if (disposedCaseSnapshot.exists()) {
        await updateDoc(disposedCaseDoc, {
          files: arrayUnion(...urls),
        });
      }
    }
  };

const needsProxy = (url) => {
  if (!url) return false;
  if (url.includes("haryanarera.gov.in")) return false;
  if (url.includes("firebasestorage.googleapis.com")) return false;
  if (url.includes("storage.googleapis.com")) return false;
  if (url.includes("jncltt.valscotech.com")) return false;
  if (url.includes("nclt-api-596718606544")) return false;
  // NGT order PDFs are served directly by greentribunal.gov.in — opening the
  // URL itself in a new tab works, the NCLT proxy doesn't accept these.
  if (url.includes("greentribunal.gov.in")) return false;
  // Supreme Court order PDFs are served directly by api.sci.gov.in — opening
  // the URL works; routing through the NCLT proxy errors.
  if (url.includes("sci.gov.in")) return false;
  return true;
};

const handleViewFile = (fileUrl) => {
  if (!fileUrl) return;

  const finalUrl = needsProxy(fileUrl)
    ? `${API_BASE_URL}/nclt/pdf?url=${encodeURIComponent(fileUrl)}`
    : fileUrl;

  window.open(finalUrl, "_blank", "noopener,noreferrer");
};

const handleDownloadFile = (fileUrl) => {
  if (!fileUrl) return;

  const finalUrl = needsProxy(fileUrl)
    ? `${API_BASE_URL}/nclt/pdf?url=${encodeURIComponent(fileUrl)}`
    : fileUrl;

  const link = document.createElement("a");
  link.href = finalUrl;
  link.download = "";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

  const handleToggleBookmark = async (fileUrl) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        toast.error('User not authenticated');
        return;
      }

      const bookmarksRef = collection(db, "lawyers", user.uid, "bookmarks");

      // Check if already bookmarked
      const existingBookmarkQuery = query(
        bookmarksRef,
        where("fileUrl", "==", fileUrl),
        where("caseNo", "==",caseNumber)
      );
      const existingBookmarkSnapshot = await getDocs(existingBookmarkQuery);

      if (!existingBookmarkSnapshot.empty) {
        // Remove from bookmarks
        const deletePromises = existingBookmarkSnapshot.docs.map(doc =>
          deleteDoc(doc.ref)
        );
        await Promise.all(deletePromises);

        setBookmarkedFiles(prev => prev.filter(url => url !== fileUrl));
        toast.success('Removed from bookmarks');
      } else {
        // Get file name from files collection
        const filesQuery = query(
  collection(db, "files"),
  where("caseNo", "==", caseNumber)
);

        const filesSnapshot = await getDocs(filesQuery);
        const fileData = filesSnapshot.docs[0]?.data() || {};
        const fileName = fileData.fileName || fileUrl.split("/").pop().split("?")[0] || "Unknown";

        // Add to bookmarks - matching Dart structure
        await addDoc(bookmarksRef, {
          fileName: fileName,
          fileUrl: fileUrl,
          caseNo: caseData.caseNumber,
          createdAt: new Date().toISOString(),
        });

        setBookmarkedFiles(prev => [...prev, fileUrl]);
        toast.success('Added to bookmarks');
      }
    } catch (error) {
      toast.error('Failed to update bookmark');
      console.error(error);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        toast.error('User not authenticated');
        return;
      }

      const pendingCaseDoc = doc(db, "pending", id);
      const disposedCaseDoc = doc(db, "disposed", id);
      const pendingCaseSnapshot = await getDoc(pendingCaseDoc);
      const disposedCaseSnapshot = await getDoc(disposedCaseDoc);

      let caseDocRef;
      if (pendingCaseSnapshot.exists()) {
        caseDocRef = pendingCaseDoc;
      } else if (disposedCaseSnapshot.exists()) {
        caseDocRef = disposedCaseDoc;
      } else {
        toast.error("Case not found.");
        return;
      }

      const newFiles = files.filter((file) => file !== fileToDelete);

      // Delete from 'files' collection
      const filesCollectionRef = collection(db, "files");
      const filesQuery = query(
        filesCollectionRef,
        where("file_url", "==", fileToDelete),
        where("lawyer_uid", "==", user.uid)
      );
      const filesSnapshot = await getDocs(filesQuery);

      // if file was uploaded by team member
if (filesSnapshot.empty) {
  const fallbackQuery = query(
    filesCollectionRef,
    where("file_url", "==", fileToDelete)
  );

  const fallbackSnap = await getDocs(fallbackQuery);
  await Promise.all(
    fallbackSnap.docs.map(d => deleteDoc(d.ref))
  );
}


      // Delete all matching file documents
      const deletePromises = filesSnapshot.docs.map((docSnapshot) =>
        deleteDoc(doc(db, "files", docSnapshot.id))
      );
      await Promise.all(deletePromises);

await updateDoc(caseDocRef, {
  files: newFiles,
});



      setFiles(newFiles);

      // Log timeline event for file deletion
      const caseCol = caseDocRef === pendingCaseDoc ? "pending" : "disposed";
      const deletedFileName = decodeURIComponent(fileToDelete.split("/").pop().split("?")[0] || "Unknown");
      addTimelineEvent(id, caseCol, {
        type: "file_removed",
        title: "File deleted",
        description: `File "${deletedFileName}" was deleted`,
        performedBy: user.uid,
        performedByName: "",
        changes: {
          "File Name": { old: deletedFileName, new: "—" },
          "Action": { old: "Present", new: "Deleted" },
        },
      });

      // Also remove from bookmarks subcollection if it was bookmarked
      if (bookmarkedFiles.includes(fileToDelete)) {
        const bookmarksQuery = query(
          collection(db, "lawyers", user.uid, "bookmarks"),
          where("fileUrl", "==", fileToDelete)
        );
        const bookmarksSnapshot = await getDocs(bookmarksQuery);
        const bookmarkDeletePromises = bookmarksSnapshot.docs.map(doc =>
          deleteDoc(doc.ref)
        );
        await Promise.all(bookmarkDeletePromises);

        setBookmarkedFiles(prev => prev.filter(url => url !== fileToDelete));
      }

      toast.success("File deleted successfully");
    } catch (error) {
      toast.error("Failed to delete file");
      console.error(error);
    } finally {
      setIsModalOpen(false);
      setFileToDelete(null);
    }
  };

  // Create a new folder
  const handleCreateFolder = async () => {
    const trimmedName = newFolderName.trim();
    if (!trimmedName) {
      toast.error("Please enter a folder name");
      return;
    }
    try {
      const user = auth.currentUser;
      if (!user) {
        toast.error("User not authenticated");
        return;
      }
      const folderDoc = {
        name: trimmedName,
        caseId: id,
        caseNo: caseData.caseNumber,
        lawyer_uid: user.uid,
        createdAt: new Date().toISOString(),
      };
      const docRef = await addDoc(collection(db, "folders"), folderDoc);
      setFolders(prev => [...prev, { id: docRef.id, ...folderDoc }]);
      setNewFolderName("");
      setShowCreateFolderModal(false);
      toast.success("Folder created successfully");
    } catch (error) {
      console.error("Error creating folder:", error);
      toast.error("Failed to create folder");
    }
  };

  // Delete a folder and move its files to root
  const handleDeleteFolder = async () => {
    if (!folderToDelete) return;
    try {
      // Remove folderId from all files in this folder
      const filesInFolderQuery = query(
        collection(db, "files"),
        where("folderId", "==", folderToDelete.id)
      );
      const filesInFolderSnap = await getDocs(filesInFolderQuery);
      for (const fileDoc of filesInFolderSnap.docs) {
        await updateDoc(doc(db, "files", fileDoc.id), { folderId: "" });
      }
      // Update local fileDetails
      setFileDetails(prev => prev.map(f =>
        f.folderId === folderToDelete.id ? { ...f, folderId: "" } : f
      ));
      // Delete the folder document
      await deleteDoc(doc(db, "folders", folderToDelete.id));
      setFolders(prev => prev.filter(f => f.id !== folderToDelete.id));
      toast.success("Folder deleted");
    } catch (error) {
      console.error("Error deleting folder:", error);
      toast.error("Failed to delete folder");
    } finally {
      setDeleteFolderModal(false);
      setFolderToDelete(null);
    }
  };

  // Drag & drop: move a file into a folder
  const handleDropOnFolder = async (folderId) => {
    if (!draggingFile) return;
    const detail = fileDetails.find(d => d.file_url === draggingFile);
    if (!detail) {
      toast.error("File details not found");
      setDraggingFile(null);
      setDragOverFolderId(null);
      return;
    }
    // Don't do anything if already in this folder
    if (detail.folderId === folderId) {
      setDraggingFile(null);
      setDragOverFolderId(null);
      return;
    }
    try {
      await updateDoc(doc(db, "files", detail.id), { folderId });
      setFileDetails(prev =>
        prev.map(f => f.file_url === draggingFile ? { ...f, folderId } : f)
      );
      const folder = folders.find(f => f.id === folderId);
      toast.success(`Moved to "${folder?.name || "folder"}"`);
    } catch (error) {
      console.error("Error moving file to folder:", error);
      toast.error("Failed to move file");
    } finally {
      setDraggingFile(null);
      setDragOverFolderId(null);
    }
  };

  // Drag & drop: move a file out of folder back to root
  const handleDropOnRoot = async () => {
    if (!draggingFile) return;
    const detail = fileDetails.find(d => d.file_url === draggingFile);
    if (!detail || !detail.folderId) {
      setDraggingFile(null);
      return;
    }
    try {
      await updateDoc(doc(db, "files", detail.id), { folderId: "" });
      setFileDetails(prev =>
        prev.map(f => f.file_url === draggingFile ? { ...f, folderId: "" } : f)
      );
      toast.success("Moved to root");
    } catch (error) {
      console.error("Error moving file to root:", error);
      toast.error("Failed to move file");
    } finally {
      setDraggingFile(null);
    }
  };

  // Toggle file selection
  const toggleFileSelection = (fileUrl) => {
    setSelectedFiles(prev => {
      const next = new Set(prev);
      if (next.has(fileUrl)) {
        next.delete(fileUrl);
      } else {
        next.add(fileUrl);
      }
      return next;
    });
  };

  // Select / deselect all visible files
  const toggleSelectAll = () => {
    const visibleFiles = getVisibleFiles();
    const allSelected = visibleFiles.every(url => selectedFiles.has(url));
    if (allSelected) {
      setSelectedFiles(new Set());
    } else {
      setSelectedFiles(new Set(visibleFiles));
    }
  };

  // Exit selection mode
  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedFiles(new Set());
  };

  // Bulk move selected files to a folder (or root)
  const handleBulkMove = async (targetFolderId) => {
    if (selectedFiles.size === 0) return;
    setBulkMoving(true);
    try {
      const batch = writeBatch(db);
      let movedCount = 0;

      for (const fileUrl of selectedFiles) {
        const detail = fileDetails.find(d => d.file_url === fileUrl);
        if (!detail || !detail.id) continue;
        // Skip if already in the target
        if ((detail.folderId || "") === (targetFolderId || "")) continue;
        batch.update(doc(db, "files", detail.id), { folderId: targetFolderId || "" });
        movedCount++;
      }

      if (movedCount === 0) {
        toast("Files are already in that location");
        setBulkMoving(false);
        setShowBulkMoveModal(false);
        return;
      }

      await batch.commit();

      // Update local state
      setFileDetails(prev =>
        prev.map(f =>
          selectedFiles.has(f.file_url) ? { ...f, folderId: targetFolderId || "" } : f
        )
      );

      const targetName = targetFolderId
        ? folders.find(f => f.id === targetFolderId)?.name || "folder"
        : "Root";
      toast.success(`Moved ${movedCount} file${movedCount > 1 ? "s" : ""} to "${targetName}"`);
      exitSelectionMode();
    } catch (error) {
      console.error("Bulk move error:", error);
      toast.error("Failed to move files");
    } finally {
      setBulkMoving(false);
      setShowBulkMoveModal(false);
    }
  };

  // Get files for current view (root = no folderId, folder = matching folderId)
  const getVisibleFiles = () => {
    let result;
    if (currentFolder) {
      // Inside a folder - show files with this folderId
      result = files.filter(url => {
        const detail = fileDetails.find(d => d.file_url === url);
        return detail && detail.folderId === currentFolder.id;
      });
    } else {
      // Root level - show files without folderId
      result = files.filter(url => {
        const detail = fileDetails.find(d => d.file_url === url);
        return !detail || !detail.folderId;
      });
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      result = result.filter(url => {
        const stored = fileDetails.find((f) => f.file_url === url);
        const fileName = stored?.fileName
          || decodeURIComponent(url.split("/").pop().split("?")[0] || "");
        return fileName.toLowerCase().includes(query);
      });
    }

    return result;
  };

  // Helper function to get file extension and type info
  const getFileInfo = (fileUrl) => {
    // Prefer the stored fileName from the file document — URL-derived names
    // can be misleading (e.g. NGT PDFs all share the same /gen_pdf_test.php
    // path, so URL parsing would show "gen_pdf_test.php" for every file).
    const stored = fileDetails.find((f) => f.file_url === fileUrl);
    const rawName = stored?.fileName || fileUrl.split("/").pop().split("?")[0] || "Unnamed File";
    const decodedFileName = stored?.fileName ? rawName : decodeURIComponent(rawName);
    const extension = (decodedFileName.split('.').pop() || '').toUpperCase();

    let fileType = 'document';
    let color = '#eab308'; // Gold color
    let bgColor = 'rgba(201, 159, 74, 0.15)';
    let Icon = FileText;

    if (['JPG', 'JPEG', 'PNG', 'HEIC'].includes(extension)) {
      fileType = 'image';
      color = '#3B82F6'; // Blue
      bgColor = 'rgba(59, 130, 246, 0.15)';
      Icon = ImageIcon;
    } else if (extension === 'PDF') {
      fileType = 'pdf';
      color = '#EF4444'; // Red
      bgColor = 'rgba(239, 68, 68, 0.15)';
      Icon = FileText;
    }

    return { fileName: decodedFileName, extension, fileType, color, bgColor, Icon };
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-[#0c0a29] text-white p-8 relative">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <button
              className="flex items-center text-white hover:text-gray-300 transition-colors"
              onClick={() => {
                if (currentFolder) {
                  setCurrentFolder(null);
                } else {
                  navigation(-1);
                }
              }}
            >
              <ArrowLeft style={{ color: "white" }} />
              <h1 className="ml-4 font-bold text-2xl" style={{ color: "#eab308" }}>
                {currentFolder ? currentFolder.name : "Case Files"}
              </h1>
            </button>
            <div className="flex items-center gap-3">
              {/* Search bar */}
              <div className="relative w-72">
                <Search size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search files..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-[#1a1a3e] border border-gray-600 rounded-lg pl-9 pr-8 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#eab308] transition-colors"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
              {/* Select / Cancel select button */}
              {selectionMode ? (
                <button
                  onClick={exitSelectionMode}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium"
                >
                  <X size={18} />
                  Cancel
                </button>
              ) : (
                <button
                  onClick={() => setSelectionMode(true)}
                  className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 px-4 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium"
                >
                  <CheckSquare size={18} />
                  Select
                </button>
              )}
              {!currentFolder && (
                <button
                  onClick={() => setShowCreateFolderModal(true)}
                  className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 px-4 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium"
                >
                  <FolderPlus size={18} />
                  Create Folder
                </button>
              )}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 px-4 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium"
              >
                <Plus size={18} />
                Add Files
              </button>
            </div>
          </div>

          {/* Breadcrumb when inside folder */}
          {currentFolder && (
            <div className="flex items-center gap-2 mb-6 text-sm text-gray-400">
              <button
                onClick={() => setCurrentFolder(null)}
                className="hover:text-white transition-colors"
              >
                Case Files
              </button>
              <ChevronRight size={14} />
              <span className="text-[#eab308]">{currentFolder.name}</span>
            </div>
          )}

          {/* Drop zone to move file back to root (shown when dragging inside a folder) */}
          {currentFolder && draggingFile && (
            <div
              className="mb-6 border-2 border-dashed border-gray-500 rounded-xl p-4 text-center text-gray-400 transition-all hover:border-[#eab308] hover:text-[#eab308]"
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
              }}
              onDrop={(e) => {
                e.preventDefault();
                handleDropOnRoot();
              }}
            >
              Drop here to move file out of folder
            </div>
          )}

          {/* Content */}
          {loading && (
            <div className="flex justify-center items-center py-20">
              <div className="w-12 h-12 border-4 border-[#eab308] border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {error && (
            <div className="text-center py-20">
              <p className="text-red-400 text-lg">{error}</p>
            </div>
          )}

          {!loading && !error && caseData && (
            <>
              {/* Drag hint banner */}
              {!currentFolder && draggingFile && folders.length > 0 && (
                <div className="mb-4 bg-[#eab308] bg-opacity-10 border border-[#eab308] border-opacity-30 rounded-lg px-4 py-2 text-[#eab308] text-sm text-center">
                  Drag and drop the file onto a folder to move it
                </div>
              )}

              {/* Folders grid (only at root level) */}
              {!currentFolder && folders.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-gray-400 text-sm font-semibold uppercase tracking-wider mb-4">Folders</h2>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                    {folders.map((folder) => {
                      const folderFileCount = fileDetails.filter(f => f.folderId === folder.id).length;
                      const isDragOver = dragOverFolderId === folder.id;
                      return (
                        <div
                          key={folder.id}
                          className={`p-2 transition-all duration-200 cursor-pointer group relative ${
                            isDragOver
                              ? 'bg-[#eab308] bg-opacity-20 border-2 border-[#eab308] border-dashed scale-105 rounded-xl'
                              : ''
                          }`}
                          onClick={() => { setSearchQuery(""); setCurrentFolder(folder); }}
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = "move";
                            setDragOverFolderId(folder.id);
                          }}
                          onDragLeave={() => setDragOverFolderId(null)}
                          onDrop={(e) => {
                            e.preventDefault();
                            setDragOverFolderId(null);
                            handleDropOnFolder(folder.id);
                          }}
                        >
                          <div className="flex flex-col items-center gap-1">
                            <div className="relative">
                              <Folder size={84} className={`transition-colors case-folder-icon ${isDragOver ? 'case-folder-icon--drag' : ''}`} fill="currentColor" />
                              {/* Delete folder button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setFolderToDelete(folder);
                                  setDeleteFolderModal(true);
                                }}
                                className="absolute -top-2 -right-3 p-1 rounded-full bg-red-100 hover:bg-red-200 text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                                title="Delete folder"
                              >
                                <Trash2 size={12} />
                              </button>
                            </div>
                            <p className="text-white text-xs font-medium text-center truncate w-full">
                              {folder.name}
                            </p>
                            <p className="text-gray-500 text-[10px]">
                              {folderFileCount} {folderFileCount === 1 ? 'file' : 'files'}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Files section */}
              {(() => {
                const visibleFiles = getVisibleFiles();
                return visibleFiles.length === 0 && (!currentFolder ? folders.length === 0 : true) ? (
                  <div className="flex flex-col items-center justify-center py-20">
                    <Folder size={100} className="text-gray-600 mb-6" />
                    <h2 className="text-2xl font-semibold text-gray-400 mb-2">
                      {currentFolder ? "No Files in Folder" : "No Files"}
                    </h2>
                    <p className="text-gray-500">
                      {currentFolder
                        ? "Add files to this folder using the Add Files button"
                        : "You have not added any files yet"}
                    </p>
                  </div>
                ) : (
                  <>
                    {visibleFiles.length > 0 && (
                      <div>
                        {!currentFolder && folders.length > 0 && (
                          <div className="flex items-center gap-3 mb-4">
                            <h2 className="text-gray-400 text-sm font-semibold uppercase tracking-wider">Files</h2>
                            {!selectionMode && (
                              <span className="text-gray-500 text-xs italic">(Drag to drop in the folder)</span>
                            )}
                          </div>
                        )}

                        {/* Selection controls */}
                        {selectionMode && visibleFiles.length > 0 && (
                          <div className="flex items-center gap-3 mb-4">
                            <button
                              onClick={toggleSelectAll}
                              className="text-sm text-[#eab308] hover:text-[#ca8a04] transition-colors flex items-center gap-1"
                            >
                              {visibleFiles.every(url => selectedFiles.has(url))
                                ? <><CheckSquare size={16} /> Deselect All</>
                                : <><Square size={16} /> Select All</>
                              }
                            </button>
                            <span className="text-gray-500 text-sm">
                              {selectedFiles.size} selected
                            </span>
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-6">
                          {visibleFiles.map((fileUrl, index) => {
                            const { fileName, extension, color, bgColor, Icon } = getFileInfo(fileUrl);
                            const isBookmarked = bookmarkedFiles.includes(fileUrl);

                            return (
                              <div
                                key={index}
                                draggable={!selectionMode}
                                onDragStart={(e) => {
                                  if (selectionMode) { e.preventDefault(); return; }
                                  setDraggingFile(fileUrl);
                                  e.dataTransfer.effectAllowed = "move";
                                  if (e.target) {
                                    e.target.style.opacity = '0.5';
                                  }
                                }}
                                onDragEnd={(e) => {
                                  setDraggingFile(null);
                                  setDragOverFolderId(null);
                                  if (e.target) {
                                    e.target.style.opacity = '1';
                                  }
                                }}
                                className={`bg-white rounded-2xl p-4 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer relative ${
                                  draggingFile === fileUrl ? 'opacity-50 ring-2 ring-[#eab308]' : ''
                                } ${selectionMode && selectedFiles.has(fileUrl) ? 'ring-2 ring-[#eab308]' : ''}`}
                                onClick={() => {
                                  if (selectionMode) {
                                    toggleFileSelection(fileUrl);
                                  } else {
                                    handleViewFile(fileUrl);
                                  }
                                }}
                              >
                                {/* Selection checkbox */}
                                {selectionMode && (
                                  <div
                                    className="absolute top-1/2 -translate-y-1/2 left-3 z-10"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleFileSelection(fileUrl);
                                    }}
                                  >
                                    {selectedFiles.has(fileUrl) ? (
                                      <CheckSquare size={22} className="text-[#eab308]" />
                                    ) : (
                                      <Square size={22} className="text-gray-400" />
                                    )}
                                  </div>
                                )}
                                <div className={`flex items-center justify-between gap-3 ${selectionMode ? 'pl-8' : ''}`}>
                                  {/* Left side - File info */}
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-gray-600 mb-1">
                                      File Name:
                                    </p>
                                    <h3 className="text-sm font-bold text-gray-900 mb-3 line-clamp-2 break-all">
                                      {fileName}
                                    </h3>

                                    {/* Actions row */}
                                    <div className="flex items-center gap-3 flex-wrap">
                                      {/* File type badge */}
                                      <span
                                        className="px-3 py-1 rounded-lg text-xs font-bold"
                                        style={{
                                          backgroundColor: bgColor,
                                          color: color,
                                        }}
                                      >
                                        {extension}
                                      </span>

                                      {/* Download button */}
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDownloadFile(fileUrl);
                                        }}
                                        className="p-2 rounded-lg bg-green-50 hover:bg-green-100 text-green-600 transition-colors"
                                        title="Download"
                                      >
                                        <Download size={18} />
                                      </button>

                                      {/* Bookmark button */}
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleToggleBookmark(fileUrl);
                                        }}
                                        className={`p-2 rounded-lg transition-colors ${
                                          isBookmarked
                                            ? 'bg-[#eab308] bg-opacity-15 text-[#eab308]'
                                            : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                                        }`}
                                        title={isBookmarked ? "Remove bookmark" : "Add bookmark"}
                                      >
                                        <Bookmark
                                          size={18}
                                          fill={isBookmarked ? 'currentColor' : 'none'}
                                        />
                                      </button>

                                      {/* Delete button */}
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setFileToDelete(fileUrl);
                                          setIsModalOpen(true);
                                        }}
                                        className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 transition-colors"
                                        title="Delete"
                                      >
                                        <Trash2 size={18} />
                                      </button>
                                    </div>
                                  </div>

                                  {/* Right side - Preview/Icon */}
                                  <div className="flex-shrink-0">
                                    <div className="w-16 h-16 rounded-xl bg-gray-100 border-2 border-gray-300 flex items-center justify-center relative overflow-hidden group">
                                      <Icon
                                        size={40}
                                        style={{ color: color, opacity: 0.3 }}
                                      />
                                      {/* Preview overlay */}
                                      <div className="absolute inset-0 bg-black bg-opacity-70 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                        <Eye size={24} className="text-white" />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </>
          )}

          {/* Floating bulk action bar */}
          {selectionMode && selectedFiles.size > 0 && (
            <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 light-mode-selection-bar bg-[#1a1a3e] border border-[#eab308] rounded-2xl shadow-2xl px-6 py-3 flex items-center gap-4">
              <span className="text-white text-sm font-medium">
                {selectedFiles.size} file{selectedFiles.size > 1 ? "s" : ""} selected
              </span>
              <button
                onClick={() => setShowBulkMoveModal(true)}
                className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 px-5 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium"
              >
                <FolderInput size={18} />
                Move to Folder
              </button>
              <button
                onClick={exitSelectionMode}
                className="text-gray-400 hover:text-white transition-colors p-1"
                title="Cancel"
              >
                <X size={18} />
              </button>
            </div>
          )}

          {/* Bulk Move Modal - Folder Picker */}
          {showBulkMoveModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="create-folder-modal bg-[#1a1a2e] p-8 rounded-2xl shadow-2xl max-w-md w-full">
                <h2 className="text-2xl font-bold mb-2 text-white">Move Files</h2>
                <p className="text-gray-400 text-sm mb-6">
                  Select destination for {selectedFiles.size} file{selectedFiles.size > 1 ? "s" : ""}
                </p>

                <div className="space-y-2 max-h-64 overflow-y-auto mb-6">
                  {/* Root option */}
                  {currentFolder && (
                    <button
                      onClick={() => handleBulkMove("")}
                      disabled={bulkMoving}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-[#0c0a29] hover:bg-[#2a2a4a] border border-gray-700 hover:border-[#eab308] transition-colors text-left"
                    >
                      <Folder size={20} className="text-gray-400" />
                      <span className="text-white text-sm font-medium">Root (No Folder)</span>
                    </button>
                  )}

                  {/* Folder options */}
                  {folders
                    .filter(f => !currentFolder || f.id !== currentFolder.id)
                    .map(folder => {
                      const folderFileCount = fileDetails.filter(f => f.folderId === folder.id).length;
                      return (
                        <button
                          key={folder.id}
                          onClick={() => handleBulkMove(folder.id)}
                          disabled={bulkMoving}
                          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-[#0c0a29] hover:bg-[#2a2a4a] border border-gray-700 hover:border-[#eab308] transition-colors text-left"
                        >
                          <Folder size={20} className="text-[#eab308]" fill="currentColor" />
                          <div className="flex-1">
                            <span className="text-white text-sm font-medium">{folder.name}</span>
                            <span className="text-gray-500 text-xs ml-2">
                              ({folderFileCount} file{folderFileCount !== 1 ? "s" : ""})
                            </span>
                          </div>
                        </button>
                      );
                    })}

                  {folders.filter(f => !currentFolder || f.id !== currentFolder.id).length === 0 && !currentFolder && (
                    <p className="text-gray-500 text-sm text-center py-4">
                      No folders available. Create a folder first.
                    </p>
                  )}
                </div>

                {bulkMoving && (
                  <div className="flex justify-center mb-4">
                    <div className="w-6 h-6 border-2 border-[#eab308] border-t-transparent rounded-full animate-spin" />
                  </div>
                )}

                <div className="flex justify-end">
                  <button
                    className="light-button-text px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                    onClick={() => setShowBulkMoveModal(false)}
                    disabled={bulkMoving}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            className="hidden"
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.heic"
          />

          {/* Create Folder Modal */}
          {showCreateFolderModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="create-folder-modal bg-[#1a1a2e] p-8 rounded-2xl shadow-2xl max-w-md w-full">
                <h2 className="text-2xl font-bold mb-4 text-white">Create Folder</h2>
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="Enter folder name"
                  className="w-full px-4 py-3 bg-[#0c0a29] border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#eab308] mb-6"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreateFolder();
                  }}
                />
                <div className="flex justify-end gap-4">
                  <button
                    className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                    onClick={() => {
                      setShowCreateFolderModal(false);
                      setNewFolderName("");
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-gray-900 rounded-lg transition-colors"
                    onClick={handleCreateFolder}
                  >
                    Create
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete Folder Confirmation Modal */}
          {deleteFolderModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="create-folder-modal bg-[#1a1a2e] p-8 rounded-2xl shadow-2xl max-w-md w-full">
                <h2 className="text-2xl font-bold mb-4 text-white">Delete Folder</h2>
                <p className="mb-6 text-gray-300">
                  Are you sure you want to delete "{folderToDelete?.name}"? Files inside will be moved to the root level.
                </p>
                <div className="flex justify-end gap-4">
                  <button
                    className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                    onClick={() => {
                      setDeleteFolderModal(false);
                      setFolderToDelete(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                    onClick={handleDeleteFolder}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {isModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="create-folder-modal bg-[#1a1a2e] p-8 rounded-2xl shadow-2xl max-w-md w-full">
                <h2 className="text-2xl font-bold mb-4 text-white">Confirm Deletion</h2>
                <p className="mb-6 text-gray-300">
                  Are you sure you want to delete this file? This action cannot be undone.
                </p>
                <div className="flex justify-end gap-4">
                  <button
                    className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                    onClick={() => {
                      setIsModalOpen(false);
                      setFileToDelete(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                    onClick={handleDeleteConfirm}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default CaseFiles;
